import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { AlertBox, Button, Icons, Events, EventsEmitter } from 'msp-components'
import {
  useStandingOrders,
  useNavigation,
  StandingOrderType,
  OrderProductLineItem
} from 'msp-integrations'
import { translations } from '../AccountStandingOrders.constants'
import { StandingOrdersDetailActions } from '../AccountStandingOrders.types'
import OrderSubscriptionModal from '../../AccountOrders/AccountOrderDetail/OrderActions/OrderSubscriptionAction/OrderSubscriptionModal'

export const StandingOrdersDetailsActions = ({
  standingOrder,
  products
}: {
  standingOrder: StandingOrderType
  products: OrderProductLineItem[]
}) => {
  const { formatMessage } = useIntl()
  const confirmBoxRef = useRef<AlertBox>()
  const [action, setAction] = useState<StandingOrdersDetailActions>()
  const { removeList, suspend, skipNext, activate } = useStandingOrders()
  const navigate = useNavigation()
  const subscriptionModalEmitter = useRef(EventsEmitter)

  const showConfirm = (action: StandingOrdersDetailActions) => {
    setAction(action)
    confirmBoxRef?.current.showAlert()
  }

  const getConfirmationText = () => {
    switch (action) {
      case StandingOrdersDetailActions.delete:
        return formatMessage(translations.deleteConfirmationText)
      case StandingOrdersDetailActions.skip:
        return formatMessage(translations.skipConfirmationText)
      case StandingOrdersDetailActions.suspend:
        return formatMessage(translations.suspendConfirmationText)
      case StandingOrdersDetailActions.activate:
        return formatMessage(translations.activateConfirmationText)
      default:
        return ''
    }
  }

  const confirmAction = async () => {
    switch (action) {
      case StandingOrdersDetailActions.delete:
        await removeList(standingOrder.id)
        navigate(`/account/subscriptions`)
        return
      case StandingOrdersDetailActions.skip:
        await skipNext(standingOrder.id)
        break
      case StandingOrdersDetailActions.suspend:
        await suspend(standingOrder.id)
        break
      case StandingOrdersDetailActions.activate:
        await activate(standingOrder.id)
        break
    }
    confirmBoxRef?.current?.closeAlert()
    setAction(undefined)
  }

  return (
    <>
      <div className='border-machine2-75 mb-10 mt-12 flex flex-col justify-between border-b pb-10 xl:flex-row'>
        <Button
          className='mb-2 mr-0 w-full py-1 xl:mb-0 xl:mr-2 xl:w-[25%]'
          variant='outline'
          type='secondary'
          onClick={() => showConfirm(StandingOrdersDetailActions.skip)}
          heightIsAuto
        >
          {formatMessage(translations.detailActionSkipNextDelivery)}
        </Button>
        <Button
          className='w-fullmr-2 mb-2 mr-0 xl:mb-0 xl:mr-2 xl:w-[25%]'
          variant='outline'
          type='secondary'
          onClick={() =>
            showConfirm(
              standingOrder.c_orderingStatus == 1
                ? StandingOrdersDetailActions.suspend
                : StandingOrdersDetailActions.activate
            )
          }
          heightIsAuto
        >
          {formatMessage(
            standingOrder.c_orderingStatus == 1
              ? translations.detailActionSuspend
              : translations.detailActionActivate
          )}
        </Button>
        <Button
          className='mb-2 mr-0 w-full xl:mb-0 xl:mr-2 xl:w-[25%]'
          variant='outline'
          type='secondary'
          onClick={() => {
            subscriptionModalEmitter.current?.dispatch(
              Events.OPEN_SUBSCRIPTION_MODAL,
              { products }
            )
          }}
          heightIsAuto
        >
          {formatMessage(translations.detailActionChange)}
        </Button>
        <Button
          className='mb-2 w-full xl:mb-0 xl:w-[25%]'
          variant='outline'
          type='secondary'
          onClick={() => showConfirm(StandingOrdersDetailActions.delete)}
          heightIsAuto
        >
          {formatMessage(translations.detailActionDelete)}
        </Button>
        <AlertBox
          ref={confirmBoxRef}
          type='standard'
          headlineColor='text-black'
          title={formatMessage(translations.confirmBoxTitle)}
          closeButtonIcon={<Icons iconName='Close' size='base' />}
        >
          <div className='text-black'>{getConfirmationText()}</div>

          <div className='flex w-full pt-4'>
            <Button
              type={'secondary'}
              variant='outline'
              className='mr-2'
              onClick={() => confirmBoxRef?.current.closeAlert()}
            >
              {formatMessage(translations.confirmBoxCancel)}
            </Button>
            <Button type='secondary' onClick={() => confirmAction()}>
              {formatMessage(translations.confirmBoxSubmit)}
            </Button>
          </div>
        </AlertBox>
      </div>
      <OrderSubscriptionModal standingOrder={standingOrder} />
    </>
  )
}
